import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Button,
  CardBody,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import { Card } from "react-bootstrap";
import CardsHeader from "components/Headers/CardsHeader";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../hucs/util/helpers";
import { serverHost } from "variables/serverdata";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Bar } from "react-chartjs-2";

function Dashboard() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  // ---------------------------
  // STATE
  // ---------------------------
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [extensionsSummary, setExtensionsSummary] = React.useState({});
  const [devicesSummary, setDevicesSummary] = React.useState({});
  const [gatewaysSummary, setGatewaysSummary] = React.useState({});
  const [dailyData, setDailyData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [chartsError, setChartsError] = useState(null);
  const [chartLoading, setChartsLoading] = useState(false);
  const token = getAuthToken();

  const selectedSiteRaw = localStorage.getItem("selectedSite");
  const selectedSite = selectedSiteRaw ? JSON.parse(selectedSiteRaw) : null;
  const siteUuid = selectedSite?.site_uuid;

  // ---------------------------
  // LOAD DASHBOARD DATA
  // ---------------------------
  const fetchCallHistory = async () => {
    setChartsLoading(true);
    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const response = await fetch(
        `${serverHost}/call_history/${site.site_uuid}`,
      );
      if (!response.ok) throw new Error("Failed to fetch active calls");
      const data = await response.json();
      setDailyData(data.daily);
      setHourlyData(data.hourly);
    } catch (err) {
      setChartsError(err.message);
    } finally {
      setChartsLoading(false);
    }
  };
  React.useEffect(() => {
    fetchCallHistory();
    const load = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Extensions (site-scoped)
        if (siteUuid) {
          const extRes = await fetch(
            `${serverHost}/dashboard/extensions_summary`,
            {
              headers: {
                ...headers,
                "x-site-uuid": siteUuid,
              },
            },
          );
          if (extRes.ok) {
            setExtensionsSummary(await extRes.json());
          }
          const devicesRes = await fetch(
            `${serverHost}/dashboard/devices_summary`,
            {
              headers: {
                ...headers,
                "x-site-uuid": siteUuid,
              },
            },
          );
          if (devicesRes.ok) {
            setDevicesSummary(await devicesRes.json());
          }
          const gatewaysRes = await fetch(
            `${serverHost}/dashboard/gateways_summary`,
            {
              headers: {
                ...headers,
                "x-site-uuid": siteUuid,
              },
            },
          );
          if (gatewaysRes.ok) {
            setGatewaysSummary(await gatewaysRes.json());
          }
        }
      } catch (err) {
        console.error(err);
        setError("Dashboard failed to load");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token, selectedSite?.domain_uuid]);

  // --- Chart configs ---
  const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textColor = isDark ? "#cbd5e1" : "#374151";
  const tooltipBg = isDark ? "#1e293b" : "#fff";

  const dailyChartData = {
    labels: dailyData.map((d) => {
      const dt = new Date(d.day);
      return dt.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }),
    datasets: [
      {
        label: "Calls",
        data: dailyData.map((d) => d.call_count),
        backgroundColor: dailyData.map((d) =>
          d.day === selectedDate
            ? isDark
              ? "rgba(99,179,237,0.95)"
              : "rgba(37,99,235,0.85)"
            : isDark
              ? "rgba(99,179,237,0.35)"
              : "rgba(37,99,235,0.3)",
        ),
        borderColor: dailyData.map((d) =>
          d.day === selectedDate
            ? isDark
              ? "#63b3ed"
              : "#1d4ed8"
            : isDark
              ? "rgba(99,179,237,0.6)"
              : "rgba(37,99,235,0.5)",
        ),
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: isDark ? "#334155" : "#e2e8f0",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          title: (items) => dailyData[items[0].dataIndex]?.date ?? "",
          label: (item) => ` ${item.raw} calls`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor },
        ticks: {
          color: textColor,
          font: { size: 11 },
          stepSize: 1,
          precision: 0,
        },
      },
    },
  };

  // Build full 24-hour labels
  const hourlyChartData = {
    labels: hourlyData.map((h) => {
      const dt = new Date(h.hour_bucket);
      return dt.toLocaleString("en-US", {
        weekday: "short",
        hour: "numeric",
        hour12: true,
      });
    }),
    datasets: [
      {
        label: "Calls",
        data: hourlyData.map((h) => h.call_count),
        backgroundColor: hourlyData.map((h) =>
          h.day === selectedDate
            ? isDark
              ? "rgba(99,179,237,0.95)"
              : "rgba(37,99,235,0.85)"
            : isDark
              ? "rgba(99,179,237,0.35)"
              : "rgba(37,99,235,0.3)",
        ),
        borderColor: hourlyData.map((h) =>
          h.day === selectedDate
            ? isDark
              ? "#63b3ed"
              : "#1d4ed8"
            : isDark
              ? "rgba(99,179,237,0.6)"
              : "rgba(37,99,235,0.5)",
        ),
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const hourlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: isDark ? "#334155" : "#e2e8f0",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (item) => ` ${item.raw} calls`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        suggestedMax: 5,
        grid: { color: gridColor },
        ticks: {
          color: textColor,
          font: { size: 11 },
          stepSize: 1,
          precision: 0,
        },
      },
    },
  };

  const info_style = {
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderRadius: "1rem",
    overflow: "hidden",
  };

  const cardHeaderStyle = {
    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
    color: isDark ? "#e2e8f0" : "#111",
  };

  const formattedSelectedDate = selectedDate
    ? new Date(selectedDate).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

  // ---------------------------
  // RENDER STATES
  // ---------------------------
  if (loading) {
    return (
      <Container fluid className="mt--6">
        <Card>
          <CardBody className="text-center">
            <Spinner /> Loading dashboard…
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="mt--6">
        <Alert color="danger">{error}</Alert>
      </Container>
    );
  }

  // ---------------------------
  // MODULE CONFIG
  // ---------------------------
  const moduleConfig = {
    HUCS: {
      title: "Module",
      cardValue: "HUCS",
      icon: "fab fa-intercom",
      color: "primary",
      path: "/admin/hucs",
      description: "Extensions & call routing",
    },
    HRMS: {
      title: "Module",
      cardValue: "HRMS",
      icon: "fab fa-intercom",
      color: "orange",
      path: "/admin/hrms",
      description: "Enterphones",
    },
  };

  const headerCards = [
    {
      title: "Extensions",
      value: extensionsSummary.extensions ?? 0,
      icon: "fas fa-phone",
      color: "primary",
      text: "Total extensions",
      onClick: () => navigate("/admin/hucs/extensions"),
    },
    {
      title: "Devices",
      value: devicesSummary.devices ?? 0,
      icon: "fas fa-microchip",
      color: "info",
      text: "Total devices",
      onClick: () => navigate("/admin/hucs/devices"),
    },
    {
      title: "Gateways",
      value: gatewaysSummary.gateways ?? 0,
      icon: "fas fa-network-wired",
      color: "orange",
      text: "Total gateways",
      onClick: () => navigate("/admin/hucs/gateways"),
    },
    {
      title: "Issues",
      value:
        (extensionsSummary.disabled ?? 0) +
        (devicesSummary.disabled ?? 0) +
        (gatewaysSummary.disabled ?? 0),
      icon: "fas fa-exclamation-triangle",
      color:
        (extensionsSummary.disabled ?? 0) +
          (devicesSummary.disabled ?? 0) +
          (gatewaysSummary.disabled ?? 0) >
        0
          ? "danger"
          : "success",
      text: "Items needing attention",
    },
  ];
  // ---------------------------
  // DASHBOARD UI
  // ---------------------------
  return (
    <>
      <CardsHeader
        name="Dashboard"
        parentName="HUCS"
        cards={headerCards}
        className="hucs-header"
      />

      <Container fluid className="mt--6">
        <Row className="mb-4">
          <Col xl="4" md="8">
            <Card className="shadow-sm">
              <CardBody>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">Extensions</h4>

                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={() => navigate("/admin/hucs/extensions")}
                  >
                    View →
                  </Button>
                </div>

                {/* Main value */}
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-phone text-success mr-2" />
                  <h2 className="mb-0">{extensionsSummary.extensions ?? 0}</h2>
                  <span className="ml-2 text-muted">total</span>
                </div>

                {/* Secondary status */}
                <div className="d-flex align-items-center">
                  <i
                    className={`fas fa-phone-slash mr-2 ${
                      (extensionsSummary.disabled ?? 0) === 0
                        ? "text-success"
                        : "text-warning"
                    }`}
                  />
                  <span>
                    <strong>{extensionsSummary.disabled ?? 0}</strong> disabled
                  </span>
                </div>

                {/* Footer hint */}
                <small className="text-muted d-block mt-2">
                  {(extensionsSummary.disabled ?? 0) === 0
                    ? "All extensions active"
                    : "Some extensions are disabled"}
                </small>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" md="8">
            <Card className="shadow-sm">
              <CardBody>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">Devices</h4>

                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={() => navigate("/admin/hucs/devices")}
                  >
                    View →
                  </Button>
                </div>

                {/* Main value */}
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-microchip text-success mr-2" />
                  <h2 className="mb-0">{devicesSummary.devices ?? 0}</h2>
                  <span className="ml-2 text-muted">total</span>
                </div>

                {/* Secondary status */}
                <div className="d-flex align-items-center">
                  <i
                    className={`fas fa-wifi mr-2 ${
                      (devicesSummary.disabled ?? 0) === 0
                        ? "text-success"
                        : "text-danger"
                    }`}
                  />
                  <span>
                    <strong>{devicesSummary.disabled ?? 0}</strong> disabled
                  </span>
                </div>

                {/* Footer hint */}
                <small className="text-muted d-block mt-2">
                  {(devicesSummary.disabled ?? 0) === 0
                    ? "All devices enabled"
                    : "Some devices are disabled"}
                </small>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" md="8">
            <Card className="shadow-sm">
              <CardBody>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">Gateways</h4>

                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={() => navigate("/admin/hucs/gateways")}
                  >
                    View →
                  </Button>
                </div>

                {/* Main value */}
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-network-wired text-success mr-2" />
                  <h2 className="mb-0">{gatewaysSummary.gateways ?? 0}</h2>
                  <span className="ml-2 text-muted">total</span>
                </div>

                {/* Secondary status */}
                <div className="d-flex align-items-center">
                  <i
                    className={`fas fa-plug mr-2 ${
                      (gatewaysSummary.disabled ?? 0) === 0
                        ? "text-success"
                        : "text-warning"
                    }`}
                  />
                  <span>
                    <strong>{gatewaysSummary.disabled ?? 0}</strong> disabled
                  </span>
                </div>

                {/* Footer hint */}
                <small className="text-muted d-block mt-2">
                  {(gatewaysSummary.disabled ?? 0) === 0
                    ? "All gateways active"
                    : "Some gateways are disabled"}
                </small>
              </CardBody>
            </Card>
          </Col>
        </Row>

          {chartsError ? (
          <Alert variant="warning" className="mb-4">
            Could not load call history charts: {chartsError}
          </Alert>
        ) : (
          <Row className="mb-4">
            {/* Daily Chart */}
            <Col xs="12" md="6" className="mb-3">
              <Card
                className={`shadow-sm h-100 ${isDark ? "bg-dark text-light border-dark" : ""}`}
                style={info_style}
              >
                <Card.Header style={cardHeaderStyle}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">CALLS — LAST 7 DAYS</h4>
                  </div>
                </Card.Header>
                <Card.Body
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#fff",
                    height: "260px",
                  }}
                >
                  {chartLoading ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      <Spinner animation="border" variant={isDark ? "light" : "primary"} />
                      <span style={{marginLeft: "10px"}}>Loading last 7 days, Please Wait</span>
                    </div>
                  ) : dailyData.length === 0 ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      No data available
                    </div>
                  ) : (
                    <Bar data={dailyChartData} options={dailyChartOptions} />
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Hourly Chart */}
            <Col xs="12" md="6" className="mb-3">
              <Card
                className={`shadow-sm h-100 ${isDark ? "bg-dark text-light border-dark" : ""}`}
                style={info_style}
              >
                <Card.Header style={cardHeaderStyle}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">CALLS BY HOUR — LAST 24 HOURS</h4>
                    {selectedDate && (
                      <small style={{ color: isDark ? "#94a3b8" : "#6c757d" }}>
                        {formattedSelectedDate}
                      </small>
                    )}
                  </div>
                </Card.Header>
                <Card.Body
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#fff",
                    height: "260px",
                  }}
                >
                  {chartLoading ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      <Spinner animation="border" variant={isDark ? "light" : "primary"} />
                      <span style={{marginLeft: "10px"}}>Loading last 24 hours, Please Wait</span>
                    </div>
                  ) :!hourlyData ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      No Data Available
                    </div>
                  ) : (
                    <Bar data={hourlyChartData} options={hourlyChartOptions} />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
}

export default Dashboard;
